import React from 'react';
import { Card, Button, Badge, Spinner, Alert, Row, Col, Nav, Modal, Form } from 'react-bootstrap';
import {
    FaEye,
    FaBan,
    FaUserTie,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaHandshake,
    FaFileContract,
    FaExclamationTriangle,
    FaCheckCircle,
    FaClock,
    FaFilter,
    FaTimesCircle,
    FaTimes
} from 'react-icons/fa';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import HeaderTopSection from "../../components/partials/HeaderTop";
import Banner from "../../components/partials/Banner";
import ClientEOIDetailModal from '../../components/modals/professionalCandidate/ClientEOIDetailModal2';
import useValidationRequest from './hooks/useValidationRequest';

const ValidationRequestPage = () => {
    const {
        eois,
        filteredEois,
        activeFilter,
        isLoading,
        error,
        selectedEOI,
        isModalOpen,
        cancellingId,
        creatingContractId,
        isApproving,
        showRejectModal,
        selectedEOIForReject,
        rejectionReason,
        lightboxOpen,
        lightboxImages,
        lightboxIndex,
        filterStats,
        getStatusBadge,
        getStatusText,
        formatDate,
        formatCurrency,
        getServiceImage,
        getProfessionalProfilePicture,
        handleFilterChange,
        handleViewDetails,
        handleUpdate,
        handleCloseModal,
        handleApproveRequest,
        handleRejectRequest,
        confirmRejectRequest,
        closeRejectModal,
        setRejectionReason,
        handleOpenLightbox,
        handleCloseLightbox,
        fetchClientEOIs
    } = useValidationRequest();

    // ✅ Fonction pour préparer les images pour Lightbox
    const prepareLightboxImages = (eoi) => {
        const images = [];

        // Ajouter l'image principale du service
        const mainImage = getServiceImage(eoi);
        images.push({
            src: mainImage,
            alt: eoi.serviceName || 'Image du service'
        });

        // Ajouter toutes les images des médias
        if (eoi.mediaSubscriptions && eoi.mediaSubscriptions.length > 0) {
            eoi.mediaSubscriptions.forEach(media => {
                const imageUrl = media.urlPresigned || media.url;
                // Éviter les doublons avec l'image principale
                if (imageUrl !== mainImage) {
                    images.push({
                        src: imageUrl,
                        alt: media.name || 'Image'
                    });
                }
            });
        }

        return images;
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Validation des Demandes Clients"
                subtitle="Gérez et validez les demandes de service des clients"
            />

            <div className="container-fluid py-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                <div className="container">
                    <div className="row">
                        {/* Sidebar de filtrage */}
                        <div className="col-lg-3 mb-4">
                            <Card className="shadow-sm border-0 sticky-top" style={{ top: '100px', borderRadius: '16px', overflow: 'hidden' }}>
                                <Card.Header className="bg-gradient text-white border-0 py-4" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                }}>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <FaFilter className="me-2" size={20} />
                                        <h5 className="mb-0 fw-bold">Filtrer les demandes</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Nav variant="pills" className="flex-column">
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'PENDING' ? 'active-pending' : ''}`}
                                                onClick={() => handleFilterChange('PENDING')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'PENDING' ? '4px solid #ffc107' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'PENDING' ? '#fff3cd' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaClock className="me-2" style={{ color: activeFilter === 'PENDING' ? '#ffc107' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'PENDING' ? 'bold' : 'medium'}`}>En attente</span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'PENDING' ? 'warning' : 'light'}
                                                    text={activeFilter === 'PENDING' ? 'dark' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.PENDING}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'APPROVED' ? 'active-approved' : ''}`}
                                                onClick={() => handleFilterChange('APPROVED')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'APPROVED' ? '4px solid #28a745' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'APPROVED' ? '#d4edda' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaCheckCircle className="me-2" style={{ color: activeFilter === 'APPROVED' ? '#28a745' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'APPROVED' ? 'bold' : 'medium'}`}>Approuvées</span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'APPROVED' ? 'success' : 'light'}
                                                    text={activeFilter === 'APPROVED' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.APPROVED}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link
                                                className={`filter-nav-link d-flex justify-content-between align-items-center py-3 px-4 ${activeFilter === 'REJECTED' ? 'active-rejected' : ''}`}
                                                onClick={() => handleFilterChange('REJECTED')}
                                                style={{
                                                    cursor: 'pointer',
                                                    borderRadius: 0,
                                                    borderLeft: activeFilter === 'REJECTED' ? '4px solid #dc3545' : '4px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: activeFilter === 'REJECTED' ? '#f8d7da' : 'transparent'
                                                }}
                                            >
                                                <span className="d-flex align-items-center">
                                                    <FaTimesCircle className="me-2" style={{ color: activeFilter === 'REJECTED' ? '#dc3545' : '#6c757d' }} />
                                                    <span className={`fw-${activeFilter === 'REJECTED' ? 'bold' : 'medium'}`}>Rejetées</span>
                                                </span>
                                                <Badge
                                                    bg={activeFilter === 'REJECTED' ? 'danger' : 'light'}
                                                    text={activeFilter === 'REJECTED' ? 'white' : 'secondary'}
                                                    pill
                                                    className="px-3 py-2"
                                                >
                                                    {filterStats.REJECTED}
                                                </Badge>
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Contenu principal */}
                        <div className="col-lg-9">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" size="lg" />
                                    <p className="mt-3 fs-5 text-muted">Chargement des demandes...</p>
                                </div>
                            ) : error ? (
                                <Alert variant="danger" className="mx-auto" style={{ maxWidth: '600px' }}>
                                    <div className="text-center">
                                        <FaExclamationTriangle size={32} className="mb-3 text-danger" />
                                        <h5 className="alert-heading">Erreur de chargement</h5>
                                        <p className="mb-3">{error}</p>
                                        <Button variant="primary" onClick={fetchClientEOIs} size="lg">
                                            Réessayer le chargement
                                        </Button>
                                    </div>
                                </Alert>
                            ) : filteredEois.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="py-5">
                                        <FaFilter size={64} className="text-muted mb-4" />
                                        <h3 className="text-muted mb-3">
                                            {activeFilter === 'PENDING' ? 'Aucune demande en attente' :
                                                activeFilter === 'APPROVED' ? 'Aucune demande approuvée' :
                                                    'Aucune demande rejetée'}
                                        </h3>
                                        <p className="text-muted fs-5 mb-4">
                                            Aucune demande ne correspond au filtre sélectionné.
                                        </p>
                                        <Button variant="primary" size="lg" onClick={fetchClientEOIs}>
                                            Actualiser la liste
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Row xs={1} md={2} lg={3} className="g-4">
                                    {filteredEois.map(eoi => (
                                        <Col key={eoi.id}>
                                            <Card className="h-100 shadow-lg border-0 hover-lift"
                                                  style={{
                                                      borderRadius: '16px',
                                                      transition: 'all 0.3s ease',
                                                      border: eoi.clientEOIStatus === 'APPROVED' ? '2px solid #28a745' :
                                                          eoi.clientEOIStatus === 'REJECTED' ? '2px solid #dc3545' :
                                                              '2px solid #ffc107'
                                                  }}>
                                                {/* Image du service avec Lightbox */}
                                                <div
                                                    className="position-relative"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        const images = prepareLightboxImages(eoi);
                                                        handleOpenLightbox(images, 0);
                                                    }}
                                                >
                                                    <Card.Img
                                                        variant="top"
                                                        src={getServiceImage(eoi)}
                                                        style={{
                                                            height: '180px',
                                                            objectFit: 'cover',
                                                            borderTopLeftRadius: '16px',
                                                            borderTopRightRadius: '16px'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.src = "/assets/images/default-service.jpg";
                                                        }}
                                                    />
                                                    <div className="position-absolute top-0 end-0 m-3">
                                                        {getStatusBadge(eoi.clientEOIStatus)}
                                                    </div>
                                                    <div
                                                        className="position-absolute top-0 start-0 m-2 bg-dark bg-opacity-50 rounded-circle p-1"
                                                        style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    >
                                                        <FaEye size={14} color="white" />
                                                    </div>
                                                </div>

                                                <Card.Body className="d-flex flex-column p-4">
                                                    {/* Informations du client et professionnel */}
                                                    <div className="mb-3">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <div className="bg-primary rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                 style={{width: '40px', height: '40px'}}>
                                                                <FaUserTie size={16} className="text-white" />
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-0 text-dark fw-bold">Client</h6>
                                                                <small className="text-muted">
                                                                    {eoi.client?.user?.firstName || 'Non'} {eoi.client?.user?.lastName || 'spécifié'}
                                                                </small>
                                                            </div>
                                                        </div>

                                                        <div className="d-flex align-items-center mb-2">
                                                            <div className="bg-info rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                                                                 style={{width: '40px', height: '40px'}}>
                                                                <FaUserTie size={16} className="text-white" />
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-0 text-dark fw-bold">Professionnel</h6>
                                                                <small className="text-muted">
                                                                    {eoi.professional?.user?.firstName} {eoi.professional?.user?.lastName}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Informations de localisation */}
                                                    <div className="mb-3">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <FaMapMarkerAlt className="me-2 text-primary" size={14} />
                                                            <small className="text-dark fw-medium">
                                                                {eoi.serviceLocation || eoi.professional?.user?.city || 'Localisation non spécifiée'}
                                                            </small>
                                                        </div>
                                                        <div className="d-flex align-items-center mb-2">
                                                            <FaCalendarAlt className="me-2 text-primary" size={14} />
                                                            <small className="text-dark fw-medium">
                                                                Créée le {formatDate(eoi.createdAt)}
                                                            </small>
                                                        </div>
                                                        {eoi.serviceFee && (
                                                            <div className="d-flex align-items-center">
                                                                <small className="text-success fw-bold">
                                                                    {formatCurrency(eoi.serviceFee)}
                                                                </small>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Actions avec gestion conditionnelle */}
                                                    <div className="mt-auto d-flex flex-column gap-2">
                                                        <Button
                                                            variant="primary"
                                                            className="w-100"
                                                            onClick={() => handleViewDetails(eoi)}
                                                            size="lg"
                                                        >
                                                            <FaEye className="me-2" /> Voir détails
                                                        </Button>

                                                        {/* Afficher les boutons d'action uniquement pour les demandes en attente */}
                                                        {eoi.clientEOIStatus === 'PENDING' && (
                                                            <div className="d-flex gap-2">
                                                                <Button
                                                                    variant="success"
                                                                    className="flex-grow-1"
                                                                    onClick={() => handleApproveRequest(eoi)}
                                                                    disabled={isApproving === eoi.id}
                                                                    size="lg"
                                                                >
                                                                    {isApproving === eoi.id ? (
                                                                        <>
                                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                                            Approbation...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FaCheckCircle className="me-2" /> Approuver
                                                                        </>
                                                                    )}
                                                                </Button>

                                                                <Button
                                                                    variant="danger"
                                                                    className="flex-grow-1"
                                                                    onClick={() => handleRejectRequest(eoi)}
                                                                    disabled={cancellingId === eoi.id}
                                                                    size="lg"
                                                                >
                                                                    {cancellingId === eoi.id ? (
                                                                        <>
                                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                                            Rejet...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FaBan className="me-2" /> Rejeter
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        )}

                                                        {/* Messages informatifs pour les autres statuts */}
                                                        {eoi.clientEOIStatus === 'APPROVED' && (
                                                            <Alert variant="success" className="mb-0 py-2 small">
                                                                <FaCheckCircle className="me-1" /> Demande approuvée
                                                            </Alert>
                                                        )}

                                                        {eoi.clientEOIStatus === 'REJECTED' && (
                                                            <Alert variant="danger" className="mb-0 py-2 small">
                                                                <FaBan className="me-1" /> Demande rejetée
                                                            </Alert>
                                                        )}

                                                        {eoi.clientEOIStatus === 'CANCELLED' && (
                                                            <Alert variant="secondary" className="mb-0 py-2 small">
                                                                <FaBan className="me-1" /> Demande annulée par le client
                                                            </Alert>
                                                        )}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de détail */}
            {isModalOpen && selectedEOI && (
                <ClientEOIDetailModal
                    eoi={selectedEOI}
                    show={isModalOpen}
                    onClose={handleCloseModal}
                    onUpdate={handleUpdate}
                />
            )}

            {/* Modal de rejet */}
            <Modal show={showRejectModal} onHide={closeRejectModal} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <FaExclamationTriangle className="me-2" />
                        Motif du rejet
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-4">
                        Vous êtes sur le point de rejeter cette demande.
                        Veuillez indiquer le motif du rejet de manière claire et constructive.
                    </Alert>

                    {selectedEOIForReject && (
                        <div className="mb-3 p-3 bg-light rounded">
                            <strong>Détails de la demande:</strong><br/>
                            • Client: {selectedEOIForReject.client?.user?.firstName || 'Non'} {selectedEOIForReject.client?.user?.lastName || 'spécifié'}<br/>
                            • Professionnel: {selectedEOIForReject.professional?.user?.firstName} {selectedEOIForReject.professional?.user?.lastName}<br/>
                            • Service: {selectedEOIForReject.serviceName || selectedEOIForReject.professional?.selectedService}
                        </div>
                    )}

                    <Form.Group controlId="rejectionReason">
                        <Form.Label>Motif du rejet *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Veuillez expliquer les raisons du rejet de cette demande..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            required
                        />
                        <Form.Text className="text-muted">
                            Cette information sera visible par le client et le professionnel concerné.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeRejectModal} disabled={cancellingId}>
                        <FaTimes className="me-2" /> Annuler
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmRejectRequest}
                        disabled={cancellingId || !rejectionReason.trim()}
                    >
                        {cancellingId ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Rejet en cours...
                            </>
                        ) : (
                            <>
                                <FaBan className="me-2" /> Confirmer le rejet
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Lightbox pour les images */}
            <Lightbox
                open={lightboxOpen}
                close={handleCloseLightbox}
                slides={lightboxImages}
                index={lightboxIndex}
                styles={{
                    container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
                    button: { color: 'white' }
                }}
                on={{
                    view: ({ index }) => {
                        // Mettre à jour l'index quand l'utilisateur change d'image
                        // (optionnel, pour suivre l'image courante)
                    }
                }}
            />

            <style jsx>{`
                .hover-lift:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 12px 25px rgba(0,0,0,0.15) !important;
                }
                .swal2-popup-custom {
                    border-radius: 16px !important;
                }
                .sticky-top {
                    position: sticky;
                    z-index: 100;
                }
                .filter-nav-link {
                    position: relative;
                }
                .filter-nav-link:hover {
                    background-color: #f8f9fa !important;
                }
                .filter-nav-link.active-pending:hover {
                    background-color: #fff3cd !important;
                }
                .filter-nav-link.active-approved:hover {
                    background-color: #d4edda !important;
                }
                .filter-nav-link.active-rejected:hover {
                    background-color: #f8d7da !important;
                }
            `}</style>
        </>
    );
};

export default ValidationRequestPage;